import type { LoadedItem } from '../dto/searchResultHit';
import type { ItemAccessControl } from '../dto/itemAccessControlDto';
import { isInstanceOfItemAccessControlDto, toAccessControlDto, getDefaultVisibleTo, getDefaultUpdatableBy, validateAccessControlUsersCap, type AclValidationResult } from '../dto/itemAccessControlUtil';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { canMakeVisibleToGroups, canMakeVisibleToSelf, canMakeVisibleToUsers, isSuperAdmin, isTenantAdmin } from './security';
import { getDefaultGroupId } from '../dto/tenantModel';
import type UserDto from '../dto/userDto';
import type { FolderKind } from '../dto/folderModels';

type ACCESS_STATUS = 'allowed' | 'ownerMismatch' | 'groupMismatch';
export type AccessControlTypes = 'updatableBy' | 'visibleTo';

// Info about a user to determine if the user passes an item access control check.
export type UserAccessControlParams = {
  userId: string;
  userGroups: string[];
  isSuperOrTenantAdmin: boolean;
  isInOrganizationGroup: boolean;
};

export function accessControlParamsForUser(tenantId: string, user: UserDto): UserAccessControlParams {
  const superAdmin = isSuperAdmin(user.permissions);
  const tenantAdmin = isTenantAdmin(user.permissions);
  return {
    userId: user.id,
    userGroups: user.groups,
    isSuperOrTenantAdmin: superAdmin || tenantAdmin,
    isInOrganizationGroup: user.groups.includes(getDefaultGroupId(tenantId)),
  };
}

/**
 * Warning: The 'addVisibilityCondition' open search query function in '@mimir/core/search/queryUtils'
 * is tied to the implementation of this function. So if you modify this function implementation,
 * make sure to keep 'addVisibilityCondition' function in sync.
 */
export const getVisibilityStatusOfItem = (visibleTo: ItemAccessControl | undefined, itemCreatedBy: string | undefined, user: UserAccessControlParams): ACCESS_STATUS => {
  if (visibleTo && !isInstanceOfItemAccessControlDto(visibleTo)) {
    // If visibleTo is defined but corrupt
    // we throw to avoid unintentionally granting access.
    throw Error('Bad visibleTo');
  }
  const { userId, userGroups, isSuperOrTenantAdmin, isInOrganizationGroup } = user;

  const isRequestedByOwner = itemCreatedBy === userId;

  // Owners are always allowed to 'view' the item
  const alwaysAllowed = isSuperOrTenantAdmin || isRequestedByOwner;
  if (alwaysAllowed) return 'allowed';

  if (!visibleTo) {
    // If item has no visibleTo property, it's considered visible to everyone in the organization group
    return isInOrganizationGroup ? 'allowed' : 'groupMismatch';
  }

  const { level, groups, users } = visibleTo;
  const isUserPartOfAllowedGroups = groups && userGroups.some(group => groups.includes(group));
  const isUserDirectlyAllowed = users && users.includes(userId);

  if (level) {
    if (isAccessibleToEveryoneInOrganization(visibleTo)) return isInOrganizationGroup ? 'allowed' : 'groupMismatch';
    if (level === 'owner') return isRequestedByOwner ? 'allowed' : 'ownerMismatch';
    if (level === 'selected_groups') return (isUserPartOfAllowedGroups || isUserDirectlyAllowed) ? 'allowed' : 'groupMismatch';
  }

  return (isUserPartOfAllowedGroups || isUserDirectlyAllowed) ? 'allowed' : 'groupMismatch';
};

export const getUpdatabilityStatusOfItem = (
  updatableBy: ItemAccessControl | undefined,
  itemCreatedBy: string | undefined,
  user: UserAccessControlParams,
): ACCESS_STATUS => {
  if (updatableBy && !isInstanceOfItemAccessControlDto(updatableBy)) {
    // If updatableBy is defined but corrupt
    // we throw to avoid unintentionally granting access.
    throw Error('Bad updatableBy');
  }
  const { userId, userGroups, isSuperOrTenantAdmin, isInOrganizationGroup } = user;

  if (isSuperOrTenantAdmin) return 'allowed';

  const isRequestedByOwner = itemCreatedBy === userId;

  if (!updatableBy) {
    // If item has no updatableBy, it's considered to be updatable by everyone in the organization group and the owner
    return (isInOrganizationGroup || isRequestedByOwner) ? 'allowed' : 'groupMismatch';
  }

  const { level, groups, users } = updatableBy;
  const isUserPartOfAllowedGroups = groups && userGroups.some(group => groups.includes(group));
  const isUserDirectlyAllowed = users && users.includes(userId);

  if (level) {
    if (isAccessibleToEveryoneInOrganization(updatableBy)) return isInOrganizationGroup ? 'allowed' : 'groupMismatch';
    if (level === 'owner') return isRequestedByOwner ? 'allowed' : 'ownerMismatch';
    if (level === 'selected_groups') return (isUserPartOfAllowedGroups || isUserDirectlyAllowed) ? 'allowed' : 'groupMismatch';
  }

  return (isUserPartOfAllowedGroups || isUserDirectlyAllowed) ? 'allowed' : 'groupMismatch';
};

export const isOwner = (item: Pick<LoadedItem, 'createdBy' | 'owner'>, userId: string) => {
  return item?.createdBy === userId || item?.owner === userId;
};

export const canModifyAccessControl = (item: Pick<LoadedItem, 'createdBy' | 'owner'>, userId: string, hasSuperAccess: boolean) => {
  return isOwner(item, userId) || hasSuperAccess;
};

export const canSetVisibility = (
  item: Pick<LoadedItem, 'createdBy' | 'owner'>,
  userId: string,
  userPermissions: string[],
  tenantId: string,
  userGroups: string[]
) => {
  const isAdmin = isSuperAdmin(userPermissions) || isTenantAdmin(userPermissions);
  const organizationGroupId = getDefaultGroupId(tenantId);
  // A user is considered to have a custom primary group if:
  // - user is in the organization group
  // - the first group in the user's group list is not the organization group; this means that the user was assigned a primary group using
  //   the 'group priority' mechanism
  const doesUserHaveACustomPrimaryGroup = userGroups.includes(organizationGroupId) && userGroups[0] !== organizationGroupId;
  return isAdmin || canMakeVisibleToGroups(userPermissions) || doesUserHaveACustomPrimaryGroup || isOwner(item, userId);
};

export const canModifyUpdatableBy = (item: Pick<LoadedItem, 'createdBy' | 'owner'>, userId: string, hasSuperAccess: boolean) => {
  return canModifyAccessControl(item, userId, hasSuperAccess);
};

/**
 * Returns true if there's any change to access control object.
 * Compares sanitized _and_ non-sanitized version for backwards compatibility
 */
export const hasChangesToAccessControl = (currentAccessControl: ItemAccessControl | undefined, nonSanitizedUpdate: ItemAccessControl) => {
  const sanitizedUpdateWithoutValidation = sanitizeAccessControl(nonSanitizedUpdate);
  return !isEqual(sanitizedUpdateWithoutValidation, currentAccessControl) && !isEqual(nonSanitizedUpdate, currentAccessControl);
};

export const isAccessibleToEveryoneInOrganization = (access?: ItemAccessControl) => !access || access.level === 'tenant';

type SanitizedAccess = {
  sanitizedAccess: ItemAccessControl;
  errorMsg?: never;
} | {
  errorMsg: string;
  sanitizedAccess?: never;
};

const canEditIndividualUsers = (permissions: string[]): boolean =>
  isSuperAdmin(permissions) || isTenantAdmin(permissions) || canMakeVisibleToUsers(permissions);

const isSameSetOfUsers = (submitted: string[] | undefined, existing: string[] | undefined): boolean => {
  const submittedSet = new Set(submitted ?? []);
  const existingSet = new Set(existing ?? []);
  return submittedSet.size === existingSet.size && [...submittedSet].every(user => existingSet.has(user));
};

const validateUsers = (submitted: string[] | undefined, existing: string[] | undefined, canEditUsers: boolean): AclValidationResult => {
  const cap = validateAccessControlUsersCap(submitted);
  if (!cap.isValid) return cap;

  if (canEditUsers || isSameSetOfUsers(submitted, existing)) return { isValid: true };
  return { isValid: false, reason: 'User doesn\'t have enough permissions to change the individual users on this item!' };
};

export const dropUsersIfNotAllowed = (access: ItemAccessControl, permissions: string[]): ItemAccessControl =>
  canEditIndividualUsers(permissions) ? access : { ...access, users: [] };

export const getSanitizedVisibility = (
  access: ItemAccessControl,
  owner: string,
  currentUserId: string,
  currentUserPermissions: string[],
  tenantId: string,
  currentUserGroups: string[],
  existingAccess: ItemAccessControl | undefined,
): SanitizedAccess => {
  const isAdmin = isSuperAdmin(currentUserPermissions) || isTenantAdmin(currentUserPermissions);

  const users = validateUsers(access.users, existingAccess?.users, canEditIndividualUsers(currentUserPermissions));
  if (!users.isValid) return { errorMsg: users.reason };

  if (access.level === 'tenant') {
    // Allow changing visibility from undefined to 'tenant' level as an item not having the visibleTo
    // property is the same as being visible to everyone in the tenant.
    // Otherwise, check if the user has necessary permissions to change an item's visibility from potentially
    // more restrictive to open for everyone.
    const allowedToSetVisibilityToEveryone = existingAccess?.level === 'tenant'
      || !existingAccess
      || canSetVisibility({ owner }, currentUserId, currentUserPermissions, tenantId, currentUserGroups);

    if (!allowedToSetVisibilityToEveryone) {
      return { errorMsg: 'You are not allowed to set visibility of this item to everyone!' };
    }
    return { sanitizedAccess: sanitizeAccessControl(access) };
  }

  const canSetVisibilityToOwner = (owner === currentUserId && canMakeVisibleToSelf(currentUserPermissions)) || isAdmin;
  if (access.level === 'owner' && !canSetVisibilityToOwner) {
    return { errorMsg: 'Only owner/admins with required permissions are allowed to set access to \'owner only\'!' };
  }

  // Without explicit permission, a user may still restrict visibility to their own non-org primary group.
  if (access.level === 'selected_groups' && !(canMakeVisibleToGroups(currentUserPermissions) || isAdmin)) {
    const defaultGroupId = getDefaultGroupId(tenantId);
    const primaryGroupId = currentUserGroups[0];
    const providedGroups = access.groups ?? [];
    const settingVisibilityToPrimaryGroup = providedGroups.length === 1
      && providedGroups[0] === primaryGroupId
      && defaultGroupId !== primaryGroupId;

    if (!settingVisibilityToPrimaryGroup) {
      return { errorMsg: 'User doesn\'t have enough permissions to restrict visibility to specific groups!' };
    }
  }

  return { sanitizedAccess: sanitizeAccessControl(access) };
};

// updatableBy.users is owner-gated like updatableBy.groups, no permission.
export const getSanitizedUpdatableBy = (access: ItemAccessControl, owner: string, currentUser: string, currentUserPermissions: string[]): SanitizedAccess => {
  const isAdmin = isSuperAdmin(currentUserPermissions) || isTenantAdmin(currentUserPermissions);
  if (!canModifyUpdatableBy({ owner }, currentUser, isAdmin)) {
    return { errorMsg: 'You\'re not allowed to modify \'updatableBy\' list of this item!' };
  }

  const usersCap = validateAccessControlUsersCap(access.users);
  if (!usersCap.isValid) return { errorMsg: usersCap.reason };

  if (access.level === 'tenant') {
    return { sanitizedAccess: sanitizeAccessControl(access) };
  }

  if (access.level === 'owner' && owner !== currentUser && !isAdmin) {
    return { errorMsg: 'Only owner/admins are allowed to set access to \'owner only\'!' };
  }

  return { sanitizedAccess: sanitizeAccessControl(access) };
};

export const sanitizeAccessControl = (access: ItemAccessControl) => {
  const sanitizedAccess = cloneDeep(access);
  if (access.level === 'tenant') {
    return toAccessControlDto(access);
  }

  return toAccessControlDto(sanitizedAccess);
};

type InheritableVisibilityParams = {
  sourceItemVisibility: ItemAccessControl;
  sourceItemOwner: string;
  tenantId: string;
  currentUserId: string;
  permissions: string[];
  userGroups: string[];
  primaryGroupDefaultVisibleTo: ItemAccessControl | undefined | null;
};

/**
 * Checks if the user performing an operation that inherits visibility is allowed to set the parent
 * item's visibility to a new item. If allowed, returns the visibility, otherwise returns default
 * visibility properties that the user is allowed to set.
 */
export const getInheritableVisibilityFromItem = (params: InheritableVisibilityParams): ItemAccessControl => {
  const { sanitizedAccess } = getSanitizedVisibility(params.sourceItemVisibility, params.sourceItemOwner, params.currentUserId, params.permissions, params.tenantId, params.userGroups, undefined);
  if (sanitizedAccess) return sanitizedAccess;
  return getDefaultVisibleTo(params.tenantId, params.userGroups[0]!, false, params.primaryGroupDefaultVisibleTo);
};

type InheritableUpdatableByParams = {
  sourceItemUpdatableBy: ItemAccessControl;
  sourceItemOwner: string;
  currentUserId: string;
  permissions: string[];
  primaryGroupDefaultUpdatableBy: ItemAccessControl | undefined | null;
};

export const getInheritableUpdatableByFromItem = (params: InheritableUpdatableByParams): ItemAccessControl | undefined => {
  const { sanitizedAccess } = getSanitizedUpdatableBy(params.sourceItemUpdatableBy, params.sourceItemOwner, params.currentUserId, params.permissions);
  if (sanitizedAccess) return sanitizedAccess;
  return getDefaultUpdatableBy(params.primaryGroupDefaultUpdatableBy);
};

export const isFolderUpdatableByUser = (
  updatableBy: ItemAccessControl | undefined,
  itemCreatedBy: string | undefined,
  _folderKind: FolderKind | undefined,
  user: UserDto & { tenantId: string },
) => {
  const userParams = accessControlParamsForUser(user.tenantId, user);

  if (itemCreatedBy === userParams.userId) return true;
  const updatabilityStatus = getUpdatabilityStatusOfItem(updatableBy, itemCreatedBy, userParams);
  return updatabilityStatus === 'allowed';
};
